import { PromiseHelper } from '../../helpers/PromiseHelper';
import { FullscreenHelper } from '../../helpers/FullscreenHelper';
import { LAYOUT } from '../../constants/Constants';
import { PRESENTATION_TYPE } from '../../constants/Constants';
import { ViewerPlaybackStartEvent, ViewerPlaybackEndEvent, ViewerPlaybackPauseEvent } from '../../types/ViewerEvents.type';
import { AssetViewerViewMapper } from '../../helpers/AssetViewerViewMapper';
import { replaceParam } from 'url-params-helper';
export class VideoPresentationRenderer {
    constructor(assetViewerView, presentation) {
        this.assetViewerView = assetViewerView;
        this.presentation = presentation;
        this.container = null;
        this.image = null;
        this.video = null;
        this.isActive = false;
        this.isLoading = false;
        this.hasError = false;
    }
    get url() {
        const pageIndex = (this.presentation.details && this.presentation.details.firstPageIndex) || 0;
        return this.presentation.urls[pageIndex];
    }
    get dimensions() {
        return this.width && this.height ? { width: this.width, height: this.height } : null;
    }
    get hasMetaData() {
        return this.video || this.hasError === true ? true : false;
    }
    get config() {
        return this.assetViewerView.config;
    }
    get autoplay() {
        return (this.assetViewerView.viewerLayout.currentView === this.assetViewerView &&
            this.assetViewerView.config.VIEWER_LAYOUT === LAYOUT.HORIZONTAL);
    }
    get showPosterImage() {
        return this.config.VIDEO_POSTER_IMAGE_ENABLED === true && this.assetViewerView.assetViewerItem.previewUrl && !this.autoplay;
    }
    get width() {
        if (this._width) {
            return this._width;
        }
        if (this.presentation.details && this.presentation.details.dimensions && this.presentation.details.dimensions.width) {
            return this.presentation.details.dimensions.width;
        }
        if (this.video && this.video.videoWidth) {
            return this.video.videoWidth;
        }
        return null;
    }
    get height() {
        if (this._height) {
            return this._height;
        }
        if (this.presentation.details && this.presentation.details.dimensions && this.presentation.details.dimensions.height) {
            return this.presentation.details.dimensions.height;
        }
        if (this.video && this.video.videoHeight) {
            return this.video.videoHeight;
        }
        return null;
    }
    load() {
        this.isLoading = true;
        this.video = document.createElement('video');
        this.addEventListeners();
        if (this.showPosterImage) {
            return this.loadPoster().then(() => this.loadVideo());
        }
        return this.loadVideo();
    }
    loadPoster() {
        const defer = PromiseHelper.defer();
        const previewUrl = replaceParam('size', '1600x1600', this.assetViewerView.assetViewerItem.previewUrl);
        this.image = new Image();
        this.image.onload = () => {
            defer.resolve();
        };
        this.video.onerror = () => {
            // Needed so video is not blocked, in chrome when poster fails its results in white overlay in video until its played.
            this.video.poster = '';
            defer.resolve();
        };
        this.image.src = this.video.poster = previewUrl;
        return defer.promise;
    }
    loadVideo() {
        const defer = PromiseHelper.defer();
        this.video.onloadedmetadata = onLoadedMetadata.bind(this);
        this.video.oncanplay = onCanPlay.bind(this);
        this.video.onerror = onError.bind(this);
        this.video.onplay = onPlay.bind(this);
        this.video.onended = onEnded.bind(this);
        this.video.onpause = onPause.bind(this);
        this.video.controls = true;
        this.video['playsinline'] = true;
        this.video.setAttribute('controlsList', 'nodownload');
        this.video.title = this.assetViewerView.assetViewerItem.displayName;
        this.video.style.width = '100%';
        this.video.style.height = '100%';
        if (this.presentation.type === PRESENTATION_TYPE.PROCESSED) {
            // Mobile Safari needs an extension here (tested and no longer the case but left for now)
            this.video.type = 'video/mp4';
        }
        this.video.src = this.url;
        return defer.promise;
        function onLoadedMetadata() {
            this.isLoading = false;
            this.video.onloadedmetadata = null;
            defer.resolve({
                width: this.width,
                height: this.height
            });
        }
        function onCanPlay() {
            if (this.video) {
                if (this.autoplay) {
                    // IE11 does not support .catch
                    this.video.play().then(() => { }, console.warn);
                }
                this.video.onerror = null;
                this.video.oncanplay = null;
            }
        }
        function onError(error) {
            this.isLoading = false;
            this.hasError = true;
            this.video.onerror = null;
            this.video.oncanplay = null;
            this.video = null;
            defer.reject(error);
            if (this.isActive) {
                this.assetViewerView.eventBus.dispatch(new ViewerPlaybackEndEvent({
                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView)
                }));
                this.isActive = false;
            }
        }
        function onPlay() {
            this.isActive = true;
            this.assetViewerView.eventBus.dispatch(new ViewerPlaybackStartEvent({
                assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView)
            }));
        }
        function onEnded() {
            this.isActive = false;
            this.assetViewerView.eventBus.dispatch(new ViewerPlaybackEndEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView) }));
        }
        function onPause() {
            this.assetViewerView.eventBus.dispatch(new ViewerPlaybackPauseEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView) }));
        }
    }
    render(container) {
        this.container = container;
        return Promise.resolve().then(() => container.appendChild(this.video));
    }
    destroy() {
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
        if (this.image) {
            this.image.onload = null;
            this.image.src = '';
            this.image = null;
        }
        if (this.video) {
            this.video.pause();
            this.video.onloadedmetadata = null;
            this.video.oncanplay = null;
            this.video.onerror = null;
            this.video.onplay = null;
            this.video.onended = null;
            this.video.onpause = null;
            this.video.src = '';
            this.video.poster = '';
            this.video = null;
        }
    }
    destroyFinal() {
        this.destroy();
    }
    addEventListeners() {
        this.video.addEventListener('webkitbeginfullscreen', this.onWebkitBeginFullscreen.bind(this));
        this.video.addEventListener('webkitendfullscreen', this.onWebkitEndFullscreen.bind(this));
        if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
            this.video.addEventListener('contextmenu', e => e.preventDefault());
        }
    }
    onWebkitBeginFullscreen(event) {
        FullscreenHelper.setWebkitBeginFullscreen(this.video);
        document.body.classList.add('webkit-full-screen');
    }
    onWebkitEndFullscreen(event) {
        FullscreenHelper.setWebkitEndFullscreen();
        document.body.classList.remove('webkit-full-screen');
    }
}
//# sourceMappingURL=VideoPresentationRenderer.js.map